import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { fetchFollowCounts, fetchMyVisitSummary, fetchPlayerRatingSummary } from '../../services/socialService';
import PlayerPreview from '../sections/PlayerPreview';

function ProfileView({ createdProfile, isLoadingProfile, onRefreshProfile, statusMessage, sampleMode = false }) {
  const { token, user: authUser } = useAuth();
  const navigate = useNavigate();
  const [visits, setVisits] = useState({ totalViews: 0, uniqueVisitors: 0 });
  const [followCounts, setFollowCounts] = useState({ followersCount: 0, followingCount: 0 });
  const [ratingSummary, setRatingSummary] = useState({ averageRating: 0, ratingsCount: 0 });
  const [isLoadingSocial, setIsLoadingSocial] = useState(false);
  const [liveProfile, setLiveProfile] = useState(createdProfile || null);
  const PROFILE_STATS_DEBUG_MODE = process.env.REACT_APP_PROFILE_STATS_DEBUG_MODE === 'true';

  useEffect(() => {
    setLiveProfile(createdProfile || null);
  }, [createdProfile]);

  const handleRefresh = useCallback(() => {
    if (onRefreshProfile) {
      onRefreshProfile();
    }
  }, [onRefreshProfile]);

  useEffect(() => {
    if (sampleMode) return;
    handleRefresh();
  }, [handleRefresh, sampleMode]);

  useEffect(() => {
    if (sampleMode) {
      setIsLoadingSocial(false);
      return;
    }

    const profileUserId = liveProfile?.user;
    if (!token || !profileUserId) return;

    let isMounted = true;

    const loadSocialSummary = async () => {
      setIsLoadingSocial(true);
      const [visitsResult, countsResult, ratingsResult] = await Promise.allSettled([
        fetchMyVisitSummary(token),
        fetchFollowCounts(profileUserId),
        fetchPlayerRatingSummary(profileUserId, token),
      ]);

      if (!isMounted) return;

      if (visitsResult.status === 'fulfilled') {
        setVisits({
          totalViews: Number(visitsResult.value?.totalViews) || 0,
          uniqueVisitors: Number(visitsResult.value?.uniqueVisitors) || 0,
        });
      } else {
        setVisits({ totalViews: 0, uniqueVisitors: 0 });
      }

      if (countsResult.status === 'fulfilled') {
        setFollowCounts({
          followersCount: Number(countsResult.value?.followersCount) || 0,
          followingCount: Number(countsResult.value?.followingCount) || 0,
        });
      } else {
        setFollowCounts({ followersCount: 0, followingCount: 0 });
      }

      if (ratingsResult.status === 'fulfilled') {
        setRatingSummary({
          averageRating: Number(ratingsResult.value?.averageRating) || 0,
          ratingsCount: Number(ratingsResult.value?.ratingsCount) || 0,
        });
      } else {
        setRatingSummary({ averageRating: 0, ratingsCount: 0 });
      }
      setIsLoadingSocial(false);
    };

    loadSocialSummary();

    return () => {
      isMounted = false;
    };
  }, [liveProfile?._id, liveProfile?.user, sampleMode, token]);

  const ownerId = liveProfile?.user ? String(liveProfile.user) : '';
  const authUserId = authUser?._id || authUser?.id ? String(authUser?._id || authUser?.id) : '';
  const isOwner = Boolean(ownerId && authUserId && ownerId === authUserId);

  const socialStats = useMemo(
    () => ({
      visitsCount: sampleMode ? liveProfile?.socialStats?.visitsCount : visits.totalViews,
      followersCount: sampleMode ? liveProfile?.socialStats?.followersCount : followCounts.followersCount,
      followingCount: sampleMode ? liveProfile?.socialStats?.followingCount : followCounts.followingCount,
    }),
    [followCounts.followersCount, followCounts.followingCount, liveProfile?.socialStats?.followersCount, liveProfile?.socialStats?.followingCount, liveProfile?.socialStats?.visitsCount, sampleMode, visits.totalViews]
  );


  const resolvePrimaryAffiliationName = useCallback((memberships = [], primaryId = '') => {
    const normalizedPrimaryId = String(primaryId || '').trim();
    if (!normalizedPrimaryId) return '';

    const membership = (memberships || []).find((item) =>
      String(item?.entityId || item?.entity?._id || item?.entity?.id || '').trim() === normalizedPrimaryId
    );

    return membership?.entity?.name || '';
  }, []);

  const affiliationSummary = useMemo(() => ({
    organization:
      resolvePrimaryAffiliationName(authUser?.organizations, authUser?.primaryOrganizationId)
      || authUser?.organization?.name
      || 'Not selected',
    club:
      resolvePrimaryAffiliationName(authUser?.clubs, authUser?.primaryClubId)
      || authUser?.club?.name
      || 'Not selected',
    community:
      resolvePrimaryAffiliationName(authUser?.communities, authUser?.primaryCommunityId)
      || authUser?.community?.name
      || 'Not selected',
  }), [
    authUser?.club?.name,
    authUser?.clubs,
    authUser?.communities,
    authUser?.community?.name,
    authUser?.organization?.name,
    authUser?.organizations,
    authUser?.primaryClubId,
    authUser?.primaryCommunityId,
    authUser?.primaryOrganizationId,
    resolvePrimaryAffiliationName,
  ]);

  return (
    <div className="row g-4 justify-content-center">
      <div className="col-xl-12 col-lg-12">
        <div className="card shadow-sm border-0">
          <div className="card-body">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
              <div>
                <h2 className="h4 mb-1">{sampleMode ? 'Sample Profile' : 'My Profile'}</h2>
                <p className="text-muted mb-0">{sampleMode ? 'Preview how a complete player profile appears to teams and organizers.' : 'View your saved player profile details.'}</p>
              </div>
              {!sampleMode && <div className="d-flex gap-2">
                {onRefreshProfile && (
                  <button
                    className="btn btn-outline-primary"
                    type="button"
                    onClick={handleRefresh}
                    disabled={isLoadingProfile}
                  >
                    {isLoadingProfile ? 'Refreshing...' : 'Refresh'}
                  </button>
                )}
                <Link className="btn btn-primary" to="/profile/edit">
                  Edit Profile
                </Link>
              </div>}
            </div>
            {sampleMode && (
              <div className="alert alert-warning py-2 px-3 mb-3" role="note">
                This is a sample profile to show how a complete profile appears. Your real profile will look similar once completed.
              </div>
            )}
            {statusMessage && (
              <div className="alert alert-info py-2 px-3 mb-3" role="status">
                {statusMessage}
              </div>
            )}

            {!sampleMode && (
              <div className="card border-0 bg-light mb-3">
                <div className="card-body py-3 px-3 px-md-4">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-2">
                    <div>
                      <h3 className="h6 mb-1">Affiliation summary</h3>
                      <p className="text-muted small mb-0">Review your primary selections and manage full affiliation details from the dedicated page.</p>
                    </div>
                    <Link className="btn btn-outline-primary" to="/account/affiliation">
                      Manage Affiliation
                    </Link>
                  </div>
                  <div className="row g-2 small">
                    <div className="col-md-4"><span className="text-muted">Primary organization:</span> <span className="fw-semibold">{affiliationSummary.organization}</span></div>
                    <div className="col-md-4"><span className="text-muted">Primary club:</span> <span className="fw-semibold">{affiliationSummary.club}</span></div>
                    <div className="col-md-4"><span className="text-muted">Primary community:</span> <span className="fw-semibold">{affiliationSummary.community}</span></div>
                  </div>
                </div>
              </div>
            )}
            <PlayerPreview
              profile={liveProfile}
              debugMode={PROFILE_STATS_DEBUG_MODE}
              socialStats={socialStats}
              ratings={{
                isOwner,
                receivedAvg: ratingSummary.averageRating,
                receivedCount: ratingSummary.ratingsCount,
              }}
              socialLoading={isLoadingSocial}
              onOpenFollowers={() => navigate('/profile/followers')}
              onOpenFollowing={() => navigate('/profile/following')}
              isDemoProfile={sampleMode}
              showFollowAction={sampleMode}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfileView;
